import { useEffect, useState } from 'react';
import { subscribeLoading } from '@/utils/loading';

/** Delay before showing, so instant replies don't flash the bar. */
const SHOW_AFTER_MS = 150;
/** Minimum time on screen once shown, so it doesn't blink. */
const MIN_VISIBLE_MS = 300;

/**
 * A thin animated bar across the top of the window while any API request is in
 * flight (counted in utils/loading.ts from utils/api.ts), on every screen.
 */
export default function GlobalProgress() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let showTimer: number | undefined;
    let hideTimer: number | undefined;
    let shownAt = 0;
    let shown = false;

    const unsubscribe = subscribeLoading((pending) => {
      if (pending > 0) {
        window.clearTimeout(hideTimer);
        if (!shown && showTimer === undefined) {
          showTimer = window.setTimeout(() => {
            showTimer = undefined;
            shown = true;
            shownAt = Date.now();
            setVisible(true);
          }, SHOW_AFTER_MS);
        }
        return;
      }
      window.clearTimeout(showTimer);
      showTimer = undefined;
      if (!shown) return;
      hideTimer = window.setTimeout(() => {
        shown = false;
        setVisible(false);
      }, Math.max(0, MIN_VISIBLE_MS - (Date.now() - shownAt)));
    });

    return () => {
      unsubscribe();
      window.clearTimeout(showTimer);
      window.clearTimeout(hideTimer);
    };
  }, []);

  return (
    <div
      role="progressbar"
      data-print-hide
      aria-label="Loading"
      aria-hidden={!visible}
      className={`pointer-events-none fixed inset-x-0 top-0 z-[80] h-[3px] overflow-hidden bg-primary-100 transition-opacity duration-200 ${
        visible ? 'opacity-100' : 'opacity-0'
      }`}
    >
      <div className="global-progress-bar h-full w-1/3 rounded-full bg-primary-500" />
    </div>
  );
}
